/**
 * KUROGANE — Sistema de carrito.
 * Custom Elements: <kg-cart-drawer>, <kg-cart-items>, <kg-cart-note>,
 * <kg-cart-recommendations>.
 *
 * Contrato de eventos (docs/ARCHITECTURE.md §8):
 *   escucha   kg:cart:open        → abre el panel
 *   escucha   kg:cart:updated     → marca el panel como obsoleto si el cambio viene de fuera
 *   publica   kg.publishCart(cart, 'cart') tras CUALQUIER cambio
 *
 * Estrategia de red: un único viaje por interacción. Se pide a Shopify la sección
 * 'main-cart' junto con el cambio (Section Rendering API) y se sustituyen a la vez
 * la página del carrito y el contenido del panel (que main-cart emite dentro de un
 * <template data-cart-drawer-refresh>).
 */
(function () {
  'use strict';

  const kg = window.kg;
  if (!kg) return;
  // El tag <script> puede aparecer dos veces (panel + sección). Una sola inicialización.
  if (window.kgCartLoaded) return;
  window.kgCartLoaded = true;

  const theme = window.kgTheme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  const CART_SECTION = 'main-cart';
  const DEBOUNCE_QUANTITY = 300;
  const DEBOUNCE_NOTE = 600;

  /* ================================================================== *
   * Estado global de peticiones
   * ================================================================== */
  let inFlight = 0;

  function lockCheckout(locked) {
    document.querySelectorAll('[data-cart-checkout]').forEach((btn) => {
      btn.disabled = locked;
      if (locked) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
    });
  }

  function beginRequest() {
    inFlight += 1;
    lockCheckout(true);
  }

  function endRequest() {
    inFlight = Math.max(0, inFlight - 1);
    if (inFlight === 0) lockCheckout(false);
  }

  /* ================================================================== *
   * Utilidades
   * ================================================================== */
  function cartUrl() {
    return routes.cart || '/cart';
  }

  function buildUrl(base, params) {
    const url = new URL(base, window.location.origin);
    Object.keys(params || {}).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
        url.searchParams.set(key, params[key]);
      }
    });
    return url.toString();
  }

  function setCount(count) {
    const template = strings.cartCount || '';
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = template ? template.replace('[count]', String(count)) : String(count);
    });
  }

  // El fragmento sustituido trae el recuento real renderizado por Liquid.
  function syncCount(cart) {
    if (cart && typeof cart.item_count === 'number') {
      setCount(cart.item_count);
      return;
    }
    const source = document.querySelector('[data-cart-count-value]');
    if (source) setCount(parseInt(source.value, 10) || 0);
  }

  function fetchCartJson() {
    return kg.fetchJSON(`${cartUrl()}.js`).catch(() => null);
  }

  /* ------------------------------------------------------------------ *
   * Sustitución del HTML devuelto por la Section Rendering API
   * ------------------------------------------------------------------ */
  function applySections(sections) {
    if (!sections) return;
    const html = sections[CART_SECTION];
    if (typeof html !== 'string' || html === '') return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Recordamos el foco: el nodo activo va a desaparecer.
    const activeId = document.activeElement ? document.activeElement.id : '';

    // 1. Página del carrito
    const nextPage = doc.querySelector('[data-cart-page-contents]');
    const currentPage = document.querySelector('[data-cart-page-contents]');
    if (nextPage && currentPage) {
      currentPage.replaceWith(document.importNode(nextPage, true));
    }

    // 2. Panel lateral (viaja dentro de un <template> de main-cart)
    const drawer = document.querySelector('kg-cart-drawer');
    const template = doc.querySelector('template[data-cart-drawer-refresh]');
    if (drawer && template) {
      const nextForm = template.content.querySelector('.kg-cart-drawer__form');
      const currentForm = drawer.querySelector('.kg-cart-drawer__form');
      if (nextForm && currentForm) {
        currentForm.replaceWith(document.importNode(nextForm, true));
      }
    }

    syncCount();
    // Los botones recién pintados vuelven a bloquearse si aún hay peticiones vivas.
    if (inFlight > 0) lockCheckout(true);
    restoreFocus(activeId, drawer);
  }

  function restoreFocus(activeId, drawer) {
    if (!activeId) return;
    if (document.activeElement && document.activeElement !== document.body) return;

    const target = document.getElementById(activeId);
    if (target && typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
      return;
    }
    // La línea ya no existe (se eliminó): devolvemos el foco a un punto estable.
    if (drawer && drawer.dataset.open === 'true') {
      const close = drawer.querySelector('[data-drawer-close]');
      if (close) close.focus({ preventScroll: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * Peticiones al carrito
   * ------------------------------------------------------------------ */
  async function postCart(url, body) {
    beginRequest();
    try {
      const cart = await kg.fetchJSON(url, { method: 'POST', body: JSON.stringify(body) });
      applySections(cart.sections);
      syncCount(cart);
      kg.publishCart(cart, 'cart');
      return cart;
    } finally {
      endRequest();
    }
  }

  function changeLine(line, quantity) {
    return postCart(routes.cartChange || '/cart/change.js', {
      line: line,
      quantity: quantity,
      sections: CART_SECTION,
      sections_url: cartUrl()
    });
  }

  // Refresco pasivo: el carrito cambió desde otro componente (ficha de producto).
  async function refreshCart() {
    beginRequest();
    try {
      const sections = await kg.fetchJSON(buildUrl(cartUrl(), { sections: CART_SECTION }));
      applySections(sections);
    } catch (error) {
      /* Si falla, el panel conserva su contenido anterior. */
    } finally {
      endRequest();
    }
  }

  /* ================================================================== *
   * <kg-cart-drawer>
   * ================================================================== */
  class KgCartDrawer extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.stale = false;

      this.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('[data-drawer-close]')) this.close();
        },
        { signal }
      );

      document.addEventListener('kg:cart:open', (event) => {
        this.open(event.detail && event.detail.trigger);
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      }, { signal });

      // El formulario de producto ya pidió a Shopify que re-renderizara `main-cart`
      // (ver data-cart-sections en snippets/product-buy-buttons.liquid). Si ese HTML
      // llega en el evento, lo aprovechamos: nos ahorra un GET completo justo cuando
      // el panel se está abriendo, que es el peor momento para hacer esperar.
      document.addEventListener('kg:cart:item-added', (event) => {
        const sections = event.detail && event.detail.sections;
        if (!sections) return;
        this.cancelPendingSync();
        applySections(sections);
        this.stale = false;
      }, { signal });

      // Cambios que no origina este componente (p. ej. el formulario de producto).
      document.addEventListener(kg.CART_EVENT, (event) => {
        const source = event.detail && event.detail.source;
        if (source === 'cart') return;
        this.stale = true;

        // Aplazamos un tick en vez de sincronizar ya: el mismo flujo puede estar a
        // punto de entregarnos el HTML por `kg:cart:item-added`, y entonces se
        // cancela. Si no llega, sincronizamos igualmente — así una actualización
        // de cualquier otro origen nunca se pierde.
        this.cancelPendingSync();
        this.pendingSync = window.setTimeout(() => {
          this.pendingSync = null;
          if (this.isOpen && this.stale) this.syncContents();
        }, 0);
      }, { signal });
    }

    cancelPendingSync() {
      if (!this.pendingSync) return;
      window.clearTimeout(this.pendingSync);
      this.pendingSync = null;
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.cancelPendingSync();
      if (this.isOpen) {
        kg.unlockScroll(true);
        kg.hideOverlay();
        kg.releaseFocus(this);
      }
    }

    get isOpen() {
      return this.dataset.open === 'true';
    }

    open(trigger) {
      if (this.isOpen) return;
      this.dataset.open = 'true';
      kg.lockScroll();
      kg.showOverlay(() => this.close());
      // trapFocus guarda el disparador para devolverle el foco al cerrar.
      kg.trapFocus(this, this.querySelector('[data-drawer-close]'));
      this.dispatchEvent(new CustomEvent('kg:drawer:open', { bubbles: true }));
      this.syncContents();
    }

    close() {
      if (!this.isOpen) return;
      delete this.dataset.open;
      kg.unlockScroll();
      kg.hideOverlay();
      kg.releaseFocus(this, true);
      this.dispatchEvent(new CustomEvent('kg:drawer:close', { bubbles: true }));
    }

    syncContents() {
      if (!this.stale) return;
      this.stale = false;
      refreshCart();
    }
  }
  if (!customElements.get('kg-cart-drawer')) customElements.define('kg-cart-drawer', KgCartDrawer);

  /* ================================================================== *
   * <kg-cart-items>
   * ================================================================== */
  class KgCartItems extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.timers = new WeakMap();

      this.addEventListener('change', (event) => this.onQuantity(event), { signal });
      this.addEventListener('input', (event) => this.onQuantity(event), { signal });
      this.addEventListener('click', (event) => this.onClick(event), { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    onQuantity(event) {
      const input = event.target.closest && event.target.closest('[data-quantity-input]');
      if (!input) return;

      let debounced = this.timers.get(input);
      if (!debounced) {
        debounced = kg.debounce(() => this.submit(input), DEBOUNCE_QUANTITY);
        this.timers.set(input, debounced);
      }
      debounced();
    }

    onClick(event) {
      const trigger = event.target.closest && event.target.closest('[data-remove-line]');
      if (!trigger) return;
      // Sin JS este enlace ya funciona; con JS lo hacemos sin recargar.
      event.preventDefault();
      const line = parseInt(trigger.dataset.removeLine, 10);
      const row = trigger.closest('.kg-cart-item');
      this.request(row, line, 0, null);
    }

    submit(input) {
      const row = input.closest('.kg-cart-item');
      const line = parseInt(input.dataset.line, 10);
      const previous = parseInt(input.dataset.previous, 10) || 0;
      const quantity = parseInt(input.value, 10);

      if (isNaN(quantity) || quantity < 0) {
        input.value = previous;
        return;
      }
      if (quantity === previous) return;

      this.request(row, line, quantity, input);
    }

    async request(row, line, quantity, input) {
      if (!row || isNaN(line)) return;

      this.setError(row, '');
      this.setBusy(row, true);

      try {
        await changeLine(line, quantity);
        const message = quantity === 0 ? this.dataset.announceRemoved : this.dataset.announceUpdated;
        kg.announce(message || '');
      } catch (error) {
        if (input) input.value = parseInt(input.dataset.previous, 10) || 1;
        const message = this.errorMessage(row, error);
        this.setError(row, message);
        kg.announce(message);
        this.setBusy(row, false);
      }
      // En caso de éxito la fila ya se ha sustituido por HTML nuevo: no hay que
      // limpiar el estado de carga.
    }

    errorMessage(row, error) {
      const status = error && error.status;
      // 422: cantidad no disponible. Preferimos el texto del tema con el stock real.
      if (status === 422) {
        return (
          (row && row.dataset.quantityError) ||
          (error && error.message) ||
          this.dataset.errorGeneric ||
          ''
        );
      }
      return (error && error.message) || this.dataset.errorCart || '';
    }

    setBusy(row, busy) {
      if (!row) return;
      if (busy) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
    }

    setError(row, message) {
      if (!row) return;
      const box = row.querySelector('[data-line-error]');
      if (!box) return;
      if (!message) {
        box.textContent = '';
        box.hidden = true;
        return;
      }
      box.textContent = message;
      box.hidden = false;
    }
  }
  if (!customElements.get('kg-cart-items')) customElements.define('kg-cart-items', KgCartItems);

  /* ================================================================== *
   * <kg-cart-note> — nota del pedido y mensaje de regalo
   * ================================================================== */
  class KgCartNote extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.note = this.querySelector('[data-cart-note]');
      this.giftNote = this.querySelector('[data-cart-gift-note]');
      this.giftToggle = this.querySelector('[data-cart-gift-toggle]');

      this.persist = kg.debounce(() => this.save(), DEBOUNCE_NOTE);

      if (this.note) this.note.addEventListener('input', this.persist, { signal });
      if (this.giftNote) this.giftNote.addEventListener('input', this.persist, { signal });
      if (this.giftToggle) {
        this.giftToggle.addEventListener('change', () => this.onToggle(), { signal });
      }
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    onToggle() {
      const panelId = this.giftToggle.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const enabled = this.giftToggle.checked;

      this.giftToggle.setAttribute('aria-expanded', String(enabled));
      if (panel) panel.hidden = !enabled;

      if (!enabled && this.giftNote) this.giftNote.value = '';
      if (enabled && this.giftNote) this.giftNote.focus();

      this.save();
    }

    async save() {
      if (!routes.cartUpdate) return;

      const body = {};
      if (this.note) body.note = this.note.value;

      if (this.giftNote) {
        const enabled = !this.giftToggle || this.giftToggle.checked;
        body.attributes = { 'Mensaje de regalo': enabled ? this.giftNote.value : '' };
      }

      beginRequest();
      try {
        const cart = await kg.fetchJSON(routes.cartUpdate, {
          method: 'POST',
          body: JSON.stringify(body)
        });
        kg.publishCart(cart, 'cart');
      } catch (error) {
        // El formulario sigue enviando estos campos al finalizar la compra.
      } finally {
        endRequest();
      }
    }
  }
  if (!customElements.get('kg-cart-note')) customElements.define('kg-cart-note', KgCartNote);

  /* ================================================================== *
   * <kg-cart-recommendations> — complementarios diferidos
   * ================================================================== */
  class KgCartRecommendations extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.addEventListener('submit', (event) => this.onAdd(event), { signal });

      if (!this.dataset.url || !this.dataset.productId) return;
      // Diferido de verdad: solo se pide cuando el bloque llega a ser visible
      // (en el panel, cuando el panel se abre).
      this.observer = kg.onIntersect(this, () => this.load(), { rootMargin: '0px' });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;

      const url = buildUrl(this.dataset.url, {
        product_id: this.dataset.productId,
        limit: this.dataset.limit || 4,
        intent: 'complementary',
        section_id: this.dataset.sectionId || CART_SECTION
      });

      try {
        const list = await kg.fetchSection(url, '[data-cart-recommendations-list]');
        if (!list || list.children.length === 0) return;

        const title = document.createElement('h3');
        title.className = 'kg-cart-recs__title';
        title.textContent = this.dataset.title || '';

        if (title.textContent) this.appendChild(title);
        this.appendChild(document.importNode(list, true));
      } catch (error) {
        /* Sin recomendaciones no se pinta nada. */
      }
    }

    async onAdd(event) {
      const form = event.target.closest('[data-cart-add-form]');
      if (!form || !routes.cartAdd) return;

      event.preventDefault();
      const button = form.querySelector('[name="add"]');
      if (button) button.setAttribute('aria-busy', 'true');
      beginRequest();

      try {
        const formData = new FormData(form);
        formData.append('sections', CART_SECTION);
        formData.append('sections_url', cartUrl());

        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        const payload = await response.json();

        if (!response.ok || payload.status) {
          throw new Error(payload.description || payload.message || strings.cartError || '');
        }

        applySections(payload.sections);
        kg.announce(strings.added || '');
        const cart = await fetchCartJson();
        if (cart) {
          syncCount(cart);
          kg.publishCart(cart, 'cart');
        }
      } catch (error) {
        kg.announce(error.message || strings.cartError || '');
      } finally {
        endRequest();
        if (button) button.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('kg-cart-recommendations')) {
    customElements.define('kg-cart-recommendations', KgCartRecommendations);
  }

  /* ================================================================== *
   * Editor de temas
   * ================================================================== */
  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target.querySelector('[data-cart-page-contents]')) return;
    syncCount();
  });
})();
